// ═══════════════════════════════════════════════════════════════════
// AffiliateLinkBuilder (Apr28 v11)
// ═══════════════════════════════════════════════════════════════════
// Modal that lets approved creators search/pick a product and
// instantly get a shareable affiliate link + pre-filled message.
//
// Used in two places:
//   1. As a button on CreatorDashboard ("Pick a product to share")
//   2. Triggered by the "🔗 Share with my code" button on PremiumProductCard
//      when the logged-in user is an approved creator
//
// Features:
//   • Search bar with debounced /api/products?search= query
//   • Recent products shown by default (last 12 newest)
//   • Tap a product → shows preview card + generated affiliate link
//   • Pre-filled share message (editable) with product name + price + emoji
//   • Native share sheets on mobile (WhatsApp, Instagram, FB, X, generic)
//   • Copy link button with toast
//   • Hindi/English toggle aware
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { cldThumb, cldCard } from '../utils/cloudinary';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
const SITE_BASE = typeof window !== 'undefined' ? window.location.origin : 'https://glitzandglambyjyoti.com';

const AffiliateLinkBuilder = ({ creatorCode, isOpen, onClose, presetProduct = null }) => {
  const { i18n } = useTranslation();
  const hi = i18n.language === 'hi';

  const [search, setSearch]             = useState('');
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(presetProduct);
  const [shareMessage, setShareMessage] = useState('');
  // Apr28 v13 — pagination + filtering (was hard-capped at 18 products)
  const [page, setPage]                 = useState(1);
  const [hasMore, setHasMore]           = useState(true);
  const [total, setTotal]               = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories]     = useState([]);
  const debounceRef = useRef(null);
  const scrollerRef = useRef(null);

  const PAGE_SIZE = 50; // larger default load (was 18)

  // Reset selection when reopened with a preset product
  useEffect(() => {
    if (presetProduct) setSelectedProduct(presetProduct);
  }, [presetProduct]);

  // ─── Load categories once on first open ───────────────────────
  useEffect(() => {
    if (!isOpen || categories.length > 0) return;
    fetch(`${API}/api/categories`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [isOpen, categories.length]);

  // ─── Search products (debounced + filtered + paginated) ────────
  // Reset to page 1 whenever search OR category changes
  useEffect(() => {
    if (!isOpen) return;
    if (selectedProduct) return; // not searching while a product is selected

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchProducts({ q: search, cat: activeCategory, p: 1, append: false });
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, activeCategory, isOpen, selectedProduct]);

  const fetchProducts = async ({ q = '', cat = 'all', p = 1, append = false } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        page:  String(p),
      });
      if (q && q.trim()) params.set('search', q.trim());
      if (cat && cat !== 'all') params.set('category', cat);

      const res = await fetch(`${API}/api/products?${params.toString()}`);
      const data = await res.json();
      const fetched = data.products || [];
      setTotal(data.pagination?.total || fetched.length);
      const nextHasMore = (data.pagination?.pages || 1) > p;
      setHasMore(nextHasMore);

      if (append) {
        setProducts(prev => [...prev, ...fetched]);
      } else {
        setProducts(fetched);
        // scroll the picker grid back to top on new search/filter
        try { scrollerRef.current?.scrollTo({ top: 0 }); } catch (_) {}
      }
    } catch (e) {
      console.error('[affiliate-search]', e);
      if (!append) setProducts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // ─── Infinite scroll handler ──────────────────────────────────
  const handleScroll = (e) => {
    if (loading || loadingMore || !hasMore) return;
    const el = e.currentTarget;
    // Trigger when user is within 120px of the bottom
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts({ q: search, cat: activeCategory, p: nextPage, append: true });
    }
  };

  // ─── When a product is selected, prefill the share message ─────
  useEffect(() => {
    if (!selectedProduct) return;
    const p = selectedProduct;
    const sellPrice = p.discountPrice && p.discountPrice < p.price ? p.discountPrice : p.price;
    const name = (hi && p.nameHi) ? p.nameHi : (p.name || 'beautiful piece');
    const defaultMsg = hi
      ? `नमस्ते 💕 इसे देखो — ${name} सिर्फ ₹${sellPrice} में! ✨ Glitz & Glam by Jyoti से। मेरे लिंक से ऑर्डर करो:`
      : `Hey 💕 Check out this gorgeous ${name} for just ₹${sellPrice}! ✨ From Glitz & Glam by Jyoti — shop with my link:`;
    setShareMessage(defaultMsg);
  }, [selectedProduct, hi]);

  // ─── Build the actual affiliate link ──────────────────────────
  const affiliateUrl = selectedProduct
    ? `${SITE_BASE}/product/${selectedProduct._id}?ref=${creatorCode}`
    : '';

  const fullShareText = `${shareMessage} ${affiliateUrl}`;

  // ─── Share handlers (native share API where possible) ──────────
  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: selectedProduct?.name || 'Glitz & Glam',
        text: shareMessage,
        url: affiliateUrl,
      });
    } catch (_) {
      // User cancelled — silent
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullShareText);
      toast.success(hi ? '✨ लिंक कॉपी हो गया!' : '✨ Link copied!');
    } catch (_) {
      toast.error('Could not copy. Try long-pressing the link.');
    }
  };

  // Platform-specific share URLs
  const shareUrls = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(fullShareText)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(affiliateUrl)}&quote=${encodeURIComponent(shareMessage)}`,
    twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(affiliateUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(affiliateUrl)}&text=${encodeURIComponent(shareMessage)}`,
  };

  const openShare = (platform) => {
    if (platform === 'instagram') {
      // Instagram doesn't support pre-filled web share — best we can do is
      // copy the link/text and tell them to paste in Stories/DM/bio
      handleCopy();
      toast.info(hi ? '📸 Instagram में पेस्ट करें' : '📸 Paste in Instagram — link copied!');
      return;
    }
    if (platform === 'native') {
      handleNativeShare();
      return;
    }
    const url = shareUrls[platform];
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <button style={S.closeBtn} onClick={onClose} aria-label="Close">✕</button>

        {/* Header */}
        <div style={S.header}>
          <div style={S.eyebrow}>✦ {hi ? 'क्रिएटर लिंक बिल्डर' : 'Creator Link Builder'} ✦</div>
          <h2 style={S.title}>
            {selectedProduct
              ? (hi ? 'अपना लिंक शेयर करें' : 'Share Your Link')
              : (hi ? 'कोई प्रोडक्ट चुनें' : 'Pick a Product')}
          </h2>
        </div>

        {!selectedProduct ? (
          <>
            {/* Search bar */}
            <div style={S.searchWrap}>
              <span style={S.searchIcon}>🔍</span>
              <input
                style={S.searchInput}
                placeholder={hi ? 'प्रोडक्ट खोजें...' : 'Search products by name...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* Apr28 v13 — Category filter chips */}
            {categories.length > 0 && (
              <div style={S.chipRow}>
                <button
                  type="button"
                  style={{ ...S.chip, ...(activeCategory === 'all' ? S.chipActive : {}) }}
                  onClick={() => setActiveCategory('all')}
                >
                  {hi ? 'सभी' : 'All'}
                </button>
                {categories.map(c => {
                  const slug = c.slug || (c.name || '').toLowerCase();
                  const label = hi && c.nameHi ? c.nameHi : c.name;
                  const isActive = activeCategory === slug;
                  return (
                    <button
                      key={slug}
                      type="button"
                      style={{ ...S.chip, ...(isActive ? S.chipActive : {}) }}
                      onClick={() => setActiveCategory(slug)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Apr28 v13 — Counter */}
            {!loading && products.length > 0 && (
              <div style={S.counter}>
                {hi
                  ? `${products.length} में से ${total > 0 ? total : products.length} दिखा रहे हैं`
                  : `Showing ${products.length} of ${total > 0 ? total : products.length}`}
                {hasMore && (
                  <span style={{ color: '#888', marginLeft: 6 }}>
                    {hi ? '· और लोड हो रहा है...' : '· scroll for more'}
                  </span>
                )}
              </div>
            )}

            {/* Product grid (Apr28 v13 — now with infinite scroll) */}
            {loading && products.length === 0 ? (
              <div style={S.loadingMsg}>{hi ? 'लोड हो रहा है...' : 'Loading...'}</div>
            ) : products.length === 0 ? (
              <div style={S.emptyMsg}>
                {hi ? 'कोई प्रोडक्ट नहीं मिला' : 'No products found'}
              </div>
            ) : (
              <div
                style={S.productGrid}
                ref={scrollerRef}
                onScroll={handleScroll}
              >
                {products.map(p => (
                  <button
                    key={p._id}
                    type="button"
                    style={S.productCard}
                    onClick={() => setSelectedProduct(p)}
                  >
                    <div style={S.productImg}>
                      {p.images?.[0] ? (
                        <img
                          src={cldThumb(p.images[0])}
                          alt={p.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          loading="lazy"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <span style={{ fontSize: 24 }}>💎</span>
                      )}
                    </div>
                    <div style={S.productName}>{hi && p.nameHi ? p.nameHi : p.name}</div>
                    <div style={S.productPrice}>
                      ₹{(p.discountPrice && p.discountPrice < p.price ? p.discountPrice : p.price)?.toLocaleString()}
                    </div>
                  </button>
                ))}
                {/* Loading-more indicator at the bottom of the scroll */}
                {loadingMore && (
                  <div style={{ ...S.loadingMsg, gridColumn: '1 / -1', padding: '14px 0' }}>
                    {hi ? '⏳ और प्रोडक्ट लोड हो रहे हैं...' : '⏳ Loading more...'}
                  </div>
                )}
                {!hasMore && products.length > 12 && (
                  <div style={{ ...S.endMsg, gridColumn: '1 / -1' }}>
                    {hi ? '✦ ये सभी प्रोडक्ट हैं ✦' : '✦ That\'s all our products ✦'}
                  </div>
                )}
              </div>
            )}

            {/* Helper note */}
            <div style={S.helperNote}>
              💡 {hi
                ? 'टिप: प्रोडक्ट पेज पर भी "🔗 शेयर" बटन से सीधा लिंक बना सकती हैं'
                : 'Tip: You can also tap the "🔗 Share" button on any product card while browsing'}
            </div>
          </>
        ) : (
          <>
            {/* Selected product preview */}
            <div style={S.previewCard}>
              <div style={S.previewImg}>
                {selectedProduct.images?.[0] ? (
                  <img
                    src={cldCard(selectedProduct.images[0])}
                    alt={selectedProduct.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ fontSize: 38 }}>💎</span>
                )}
              </div>
              <div style={S.previewInfo}>
                <div style={S.previewName}>
                  {hi && selectedProduct.nameHi ? selectedProduct.nameHi : selectedProduct.name}
                </div>
                <div style={S.previewPriceRow}>
                  <span style={S.previewPrice}>
                    ₹{(selectedProduct.discountPrice && selectedProduct.discountPrice < selectedProduct.price ? selectedProduct.discountPrice : selectedProduct.price)?.toLocaleString()}
                  </span>
                  {selectedProduct.discountPrice && selectedProduct.discountPrice < selectedProduct.price && (
                    <span style={S.previewMrp}>₹{selectedProduct.price.toLocaleString()}</span>
                  )}
                </div>
                <button type="button" style={S.changeProductBtn} onClick={() => setSelectedProduct(null)}>
                  ← {hi ? 'दूसरा प्रोडक्ट चुनें' : 'Pick a different product'}
                </button>
              </div>
            </div>

            {/* Affiliate link box */}
            <div style={S.linkBox}>
              <label style={S.linkLabel}>{hi ? 'आपका एफिलिएट लिंक' : 'Your Affiliate Link'}</label>
              <div style={S.linkRow}>
                <input style={S.linkInput} value={affiliateUrl} readOnly onClick={e => e.target.select()} />
                <button type="button" style={S.copyBtn} onClick={handleCopy}>
                  {hi ? '📋 कॉपी' : '📋 Copy'}
                </button>
              </div>
            </div>

            {/* Editable share message */}
            <div style={S.messageBox}>
              <label style={S.linkLabel}>
                {hi ? 'मैसेज (एडिट कर सकती हैं)' : 'Share Message (editable)'}
              </label>
              <textarea
                style={S.messageInput}
                value={shareMessage}
                onChange={e => setShareMessage(e.target.value)}
                rows={3}
              />
              <div style={S.charCount}>{shareMessage.length} chars</div>
            </div>

            {/* Share buttons */}
            <div style={S.shareSection}>
              <div style={S.shareLabel}>
                {hi ? 'कहाँ शेयर करना है?' : 'Where to share?'}
              </div>
              <div style={S.shareGrid}>
                <ShareBtn icon="💬" label="WhatsApp"   color="#25D366" onClick={() => openShare('whatsapp')} />
                <ShareBtn icon="📸" label="Instagram"  color="#E4405F" onClick={() => openShare('instagram')} />
                <ShareBtn icon="👥" label="Facebook"   color="#1877F2" onClick={() => openShare('facebook')} />
                <ShareBtn icon="✈️" label="Telegram"   color="#0088cc" onClick={() => openShare('telegram')} />
                <ShareBtn icon="🐦" label="X / Twitter" color="#1DA1F2" onClick={() => openShare('twitter')} />
                {typeof navigator !== 'undefined' && navigator.share && (
                  <ShareBtn icon="📤" label={hi ? 'अधिक' : 'More...'} color="#c9a84c" onClick={() => openShare('native')} />
                )}
              </div>
            </div>

            {/* Pro tip */}
            <div style={S.tipBox}>
              💎 {hi
                ? 'प्रो टिप: Instagram Reels में बायो लिंक के रूप में, या स्टोरीज स्वाइप-अप से शेयर करें'
                : 'Pro tip: Use as bio link in Reels, or swipe-up in Stories. Each sale earns you 10% commission.'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Share button sub-component ────────────────────────────────
const ShareBtn = ({ icon, label, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ ...S.shareBtn, '--accent': color }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <span style={{ fontSize: 22 }}>{icon}</span>
    <span style={S.shareBtnLabel}>{label}</span>
  </button>
);

// ─── Styles ──────────────────────────────────────────────────────
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(4,2,10,0.85)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    overflowY: 'auto',
  },
  modal: {
    background: 'linear-gradient(180deg, #0e0b18 0%, #100d1f 100%)',
    border: '1px solid rgba(201,168,76,0.35)',
    borderRadius: 22,
    padding: '24px 22px 26px',
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(201,168,76,0.1)',
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },

  header: {
    textAlign: 'center',
    marginBottom: 20,
    paddingTop: 4,
  },
  eyebrow: {
    color: '#c9a84c',
    fontSize: 10,
    letterSpacing: 3,
    fontWeight: 700,
    fontFamily: 'system-ui',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    color: '#f5f0e8',
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
  },

  // Search
  searchWrap: { position: 'relative', marginBottom: 14 },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 14,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '11px 14px 11px 38px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 24,
    color: '#e8e8f5',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'system-ui',
    boxSizing: 'border-box',
  },

  // Product grid
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
    marginBottom: 14,
    maxHeight: 380,
    overflowY: 'auto',
    paddingRight: 4,
  },
  productCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    padding: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#e8e8f5',
    fontFamily: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  productImg: {
    width: '100%',
    aspectRatio: '1 / 1',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productName: {
    fontSize: 11,
    color: '#e8e8f5',
    lineHeight: 1.3,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: 28,
    textAlign: 'left',
  },
  productPrice: {
    color: '#c9a84c',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'system-ui',
    textAlign: 'left',
  },

  loadingMsg: { textAlign: 'center', padding: '40px 0', color: '#888', fontSize: 14 },
  emptyMsg: { textAlign: 'center', padding: '40px 0', color: '#666', fontSize: 14 },
  // Apr28 v13 — End-of-list affordance + chip + counter styles
  endMsg: {
    textAlign: 'center',
    padding: '20px 0 6px',
    color: 'rgba(201,168,76,0.5)',
    fontSize: 11,
    letterSpacing: 2,
    fontFamily: 'system-ui',
  },
  chipRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  chip: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18,
    padding: '5px 12px',
    color: '#aaa',
    fontSize: 11,
    fontFamily: 'system-ui',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  chipActive: {
    background: 'rgba(201,168,76,0.14)',
    borderColor: 'rgba(201,168,76,0.5)',
    color: '#f0d080',
    fontWeight: 700,
  },
  counter: {
    color: '#999',
    fontSize: 11,
    fontFamily: 'system-ui',
    marginBottom: 8,
    paddingLeft: 4,
  },
  helperNote: {
    background: 'rgba(201,168,76,0.06)',
    border: '1px solid rgba(201,168,76,0.18)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 12,
    color: 'rgba(232,232,245,0.75)',
    fontFamily: 'system-ui',
    lineHeight: 1.55,
  },

  // Selected product preview
  previewCard: {
    display: 'flex',
    gap: 14,
    padding: 14,
    background: 'rgba(201,168,76,0.06)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 14,
    marginBottom: 16,
  },
  previewImg: {
    width: 90,
    height: 90,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  previewInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  previewName: {
    color: '#f5f0e8',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Georgia, serif',
    lineHeight: 1.35,
  },
  previewPriceRow: { display: 'flex', alignItems: 'baseline', gap: 8 },
  previewPrice: { color: '#c9a84c', fontSize: 17, fontWeight: 800, fontFamily: 'system-ui' },
  previewMrp: { color: '#555', fontSize: 12, textDecoration: 'line-through', fontFamily: 'system-ui' },
  changeProductBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(201,168,76,0.85)',
    fontSize: 11,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    marginTop: 4,
    fontFamily: 'system-ui',
  },

  // Link box
  linkBox: { marginBottom: 14 },
  linkLabel: {
    display: 'block',
    color: 'rgba(201,168,76,0.7)',
    fontSize: 10,
    letterSpacing: 1.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontFamily: 'system-ui',
    marginBottom: 6,
  },
  linkRow: { display: 'flex', gap: 8 },
  linkInput: {
    flex: 1,
    padding: '10px 12px',
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: 8,
    color: '#f0d080',
    fontFamily: 'monospace',
    fontSize: 12,
    outline: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  copyBtn: {
    padding: '0 18px',
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    color: '#04020a',
    border: 'none',
    borderRadius: 8,
    fontWeight: 800,
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'system-ui',
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
  },

  // Message editor
  messageBox: { marginBottom: 16 },
  messageInput: {
    width: '100%',
    padding: '11px 14px',
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    color: '#e8e8f5',
    fontSize: 13,
    fontFamily: 'system-ui',
    lineHeight: 1.55,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  charCount: {
    textAlign: 'right',
    color: '#666',
    fontSize: 10,
    marginTop: 4,
    fontFamily: 'system-ui',
  },

  // Share section
  shareSection: { marginBottom: 14 },
  shareLabel: {
    color: 'rgba(201,168,76,0.7)',
    fontSize: 10,
    letterSpacing: 1.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontFamily: 'system-ui',
    marginBottom: 10,
  },
  shareGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  shareBtn: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '10px 8px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 5,
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  shareBtnLabel: {
    color: '#e8e8f5',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'system-ui',
    letterSpacing: 0.3,
  },

  tipBox: {
    background: 'rgba(201,168,76,0.06)',
    border: '1px dashed rgba(201,168,76,0.3)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 11,
    color: 'rgba(232,232,245,0.7)',
    fontFamily: 'system-ui',
    lineHeight: 1.55,
  },
};

export default AffiliateLinkBuilder;
